import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { z } from 'zod';

// Validation schema
const authSchema = z.object({
  email: z.string().trim().email('Adresse email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre')
});

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<{email?: string, password?: string}>({});
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        // Check admin role before redirecting
        setTimeout(async () => {
          const { data: hasAdminRole } = await supabase
            .rpc('has_admin_role', { user_id: session.user.id });
          
          if (hasAdminRole) {
            navigate("/admin");
          } else {
            await supabase.auth.signOut();
            setError("Accès refusé - Permissions administrateur requises");
          }
        }, 0);
      }
    });

    // Check if user is already logged in
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const { data: hasAdminRole } = await supabase
          .rpc('has_admin_role', { user_id: session.user.id });
        
        if (hasAdminRole) {
          navigate("/admin");
        } else {
          await supabase.auth.signOut();
          setError("Session expirée - Veuillez vous reconnecter");
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setValidationErrors({});

    const startTime = new Date().toISOString();
    const userAgent = navigator.userAgent;

    try {
      // Validate input
      const validation = authSchema.safeParse({ email: email.trim(), password });
      if (!validation.success) {
        const fieldErrors: {email?: string, password?: string} = {};
        validation.error.issues.forEach(err => {
          if (err.path[0] === 'email') fieldErrors.email = err.message;
          if (err.path[0] === 'password') fieldErrors.password = err.message;
        });
        setValidationErrors(fieldErrors);
        
        // Send notification for validation failure
        try {
          await supabase.functions.invoke('notify-admin-login', {
            body: {
              email: email.trim(),
              success: false,
              timestamp: startTime,
              error: 'Données de connexion invalides',
              userAgent,
            }
          });
        } catch (notifError) {
          console.error('Failed to send notification:', notifError);
        }
        
        return;
      }

      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/admin`
          }
        });

        if (error) throw error;

        toast({
          title: "Compte créé avec succès",
          description: "Vérifiez votre email pour confirmer votre compte.",
        });
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });

        if (error) {
          // Send notification for login failure
          try {
            await supabase.functions.invoke('notify-admin-login', {
              body: {
                email: email.trim(),
                success: false,
                timestamp: startTime,
                error: error.message,
                userAgent,
              }
            });
          } catch (notifError) {
            console.error('Failed to send notification:', notifError);
          }
          throw error;
        }

        // Check if user has admin role after successful login
        if (data.user) {
          const { data: hasAdminRole, error: roleError } = await supabase
            .rpc('has_admin_role', { user_id: data.user.id });

          if (roleError) {
            console.error('Error checking admin role:', roleError);
            
            // Send notification for role check failure
            try {
              await supabase.functions.invoke('notify-admin-login', {
                body: {
                  email: email.trim(),
                  success: false,
                  timestamp: startTime,
                  error: 'Erreur de vérification des permissions',
                  userAgent,
                }
              });
            } catch (notifError) {
              console.error('Failed to send notification:', notifError);
            }
            
            throw new Error('Erreur lors de la vérification des permissions');
          }

          if (!hasAdminRole) {
            await supabase.auth.signOut();
            
            // Send notification for insufficient privileges
            try {
              await supabase.functions.invoke('notify-admin-login', {
                body: {
                  email: email.trim(),
                  success: false,
                  timestamp: startTime,
                  error: 'Permissions administrateur insuffisantes',
                  userAgent,
                }
              });
            } catch (notifError) {
              console.error('Failed to send notification:', notifError);
            }
            
            throw new Error('Accès refusé - Permissions administrateur requises');
          }

          // Send notification for successful admin login
          try {
            await supabase.functions.invoke('notify-admin-login', {
              body: {
                email: email.trim(),
                success: true,
                timestamp: startTime,
                userAgent,
              }
            });
          } catch (notifError) {
            console.error('Failed to send notification:', notifError);
          }
        }

        toast({
          title: "Connexion réussie",
          description: "Redirection vers l'administration...",
        });
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      setError(error.message || 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-black">
            Administration - Connexion Sécurisée
          </CardTitle>
          <CardDescription>
            Interface d'administration privée
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAuth} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (validationErrors.email) {
                    setValidationErrors(prev => ({ ...prev, email: undefined }));
                  }
                }}
                placeholder="votre@email.com"
                required
                autoComplete="email"
                className={validationErrors.email ? "border-destructive" : ""}
              />
              {validationErrors.email && (
                <p className="text-sm text-destructive">{validationErrors.email}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Mot de passe
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (validationErrors.password) {
                    setValidationErrors(prev => ({ ...prev, password: undefined }));
                  }
                }}
                placeholder="••••••••"
                required
                autoComplete={isSignUp ? "new-password" : "current-password"}
                className={validationErrors.password ? "border-destructive" : ""}
              />
              {validationErrors.password && (
                <p className="text-sm text-destructive">{validationErrors.password}</p>
              )}
              {isSignUp && (
                <p className="text-xs text-muted-foreground">
                  Au moins 8 caractères, une majuscule, une minuscule et un chiffre
                </p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
            >
              {isLoading ? "Connexion..." : "Se connecter"}
            </Button>
            
            {!isSignUp && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsSignUp(true)}
                  className="text-sm text-muted-foreground hover:text-primary underline"
                >
                  Créer le premier compte admin
                </button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;